// Tutorial step definitions for Canntycoon onboarding
// Each step guides the player through their first grow cycle

export const TUTORIAL_STEPS = [
  {
    id: 'welcome',
    step: 0,
    screen: null, // handled by WelcomeModal, not TutorialOverlay
    target: null,
    title: 'Welcome to Canntycoon',
    body: 'You\'re running a cannabis grow facility. Your job: keep plants healthy, manage your team, and turn a profit before you run out of cash.',
    action: null,
    completionCondition: 'TUTORIAL_DISMISS_WELCOME',
  },
  {
    id: 'select-room',
    step: 1,
    screen: 'facility',
    target: '.iso-room',
    title: 'Your Grow Room',
    body: 'This is your facility. Each room holds a batch of plants. Click your room to manage it.',
    action: 'Click the room tile to open Room Detail.',
    completionCondition: 'NAVIGATE_ROOM',
  },
  {
    id: 'check-environment',
    step: 2,
    screen: 'room',
    target: '.gauge-card',
    title: 'Monitor Your Environment',
    body: 'Temperature, humidity, and CO₂ affect plant health. The gauges show actual vs. target values. Green = on target. Yellow/red = fix needed.',
    action: 'Review the environment gauges on the left.',
    completionCondition: 'VIEWED_ROOM',
  },
  {
    id: 'set-recipe',
    step: 3,
    screen: 'room',
    target: '.recipe-controls',
    title: 'Set Your Recipe Targets',
    body: 'Adjust the recipe sliders to tell your equipment what environment to maintain. Your HVAC and irrigation will drift toward these targets.',
    action: 'Adjust at least one recipe slider.',
    completionCondition: 'SET_RECIPE_TARGET',
  },
  {
    id: 'plant-batch',
    step: 4,
    screen: 'room',
    target: '.batch-panel',
    title: 'Plant Your First Batch',
    body: 'An empty room earns nothing. Plant a batch of seeds to start growing. Bagseed OG is forgiving — perfect for learning.',
    action: 'Click "Plant Batch" and select a strain.',
    completionCondition: 'PLANT_BATCH',
  },
  {
    id: 'hire-worker',
    step: 5,
    screen: 'workforce',
    target: '.job-market',
    title: 'Hire a Grower',
    body: 'Workers handle tasks your plants need: defoliating, pest treatment, transferring between stages. Without staff, plants suffer understaffing penalties.',
    action: 'Go to Workforce and hire a Grower from the Job Market.',
    completionCondition: 'HIRE_WORKER',
  },
  {
    id: 'assign-worker',
    step: 6,
    screen: 'workforce',
    target: '.staff-table',
    title: 'Assign Them to Your Room',
    body: 'Hiring isn\'t enough — workers need to be assigned to a room to help. Use the room dropdown next to their name.',
    action: 'Assign your new worker to your grow room.',
    completionCondition: 'ASSIGN_WORKER',
  },
  {
    id: 'watch-batch-grow',
    step: 7,
    screen: 'facility',
    target: null,
    title: 'Growing — and Planning Ahead',
    body: 'Your batch is in vegetative growth. As you expand, set up dedicated rooms: a Veg Room (18h light, 60–70% humidity) and a Flower Room (12h light, 40–50% humidity). Configure each room\'s recipe before you need it. When veg is done, a worker can use the "🌸 Transfer →" task to move the batch into the waiting flower room automatically.',
    action: 'Click Next — the tutorial will advance your batch to flowering.',
    completionCondition: 'DAY_7',
  },
  {
    id: 'flowering-stage',
    step: 8,
    screen: 'room',
    target: '.batch-panel',
    title: 'Flowering Stage',
    body: 'Your batch has been flipped to flower! Over the next ~56 days THC and terpenes accumulate. Keep humidity lower (40–50%) to reduce mold risk.\n\nMulti-room tip: in your real runs, set your Flower Room recipe to 12h light before the transfer. The worker\'s "Transfer →" button appears when the batch is ready and a target room has a matching 12h recipe.',
    action: 'Check the Room screen — watch THC and Terpene bars build.',
    completionCondition: 'STAGE_FLOWERING',
  },
  {
    id: 'harvest-batch',
    step: 9,
    screen: 'room',
    target: '.harvest-btn',
    title: 'Harvest When Ready',
    body: 'The batch is ready to harvest! Your tutorial batch has been advanced so you can see the full flow. In a real run, the Harvest stage badge appears automatically when flowering finishes. Harvest quickly — terpenes and THC slowly degrade if you wait.',
    action: 'Click "Harvest Now" to collect your yield.',
    completionCondition: 'HARVEST_BATCH',
  },
  {
    id: 'sell-product',
    step: 10,
    screen: 'market',
    target: '.sell-panel',
    title: 'Sell Your Harvest',
    body: 'Head to the Market screen. Select your harvested inventory, pick a channel (Wholesale is always available), set a quantity, and sell.',
    action: 'Sell at least some of your harvest.',
    completionCondition: 'SELL_HARVEST',
  },
  {
    id: 'tutorial-complete',
    step: 11,
    screen: 'facility',
    target: null,
    title: "You're Growing!",
    body: 'You\'ve completed your first cycle. Now focus on: improving quality with better recipes, hiring more staff, and unlocking new strains and tech upgrades.',
    action: null,
    completionCondition: null, // terminal step
  },
];

export const TOTAL_TUTORIAL_STEPS = TUTORIAL_STEPS.length;

// Map action types to step completion conditions
export const ACTION_TO_CONDITION = {
  'TUTORIAL_DISMISS_WELCOME': 'TUTORIAL_DISMISS_WELCOME',
  'NAVIGATE': 'NAVIGATE_ROOM',     // only when navigating to room screen
  'SET_RECIPE_TARGET': 'SET_RECIPE_TARGET',
  'PLANT_BATCH': 'PLANT_BATCH',
  'HIRE_WORKER': 'HIRE_WORKER',
  'ASSIGN_WORKER': 'ASSIGN_WORKER',
  'HARVEST_BATCH': 'HARVEST_BATCH',
  'SELL_HARVEST': 'SELL_HARVEST',
};
